import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  createProjectRequestSchema,
  PROJECT_COLORS,
  type CreateProjectRequest,
} from '@witty-board/shared'
import { Modal } from '../../components/Modal.js'
import { useCreateProjectMutation } from '../../api/projects.js'

interface CreateProjectModalProps {
  onClose: () => void
}

/**
 * Cierra el hueco de la fase 3: la fase 2 dejó `POST /projects` y la fase 3
 * la barra lateral que los lista, pero nada juntaba las dos cosas (ver
 * docs/DECISIONS.md, fase 3). Sin edición ni archivado todavía — no los pide
 * esta fase.
 */
export function CreateProjectModal({ onClose }: CreateProjectModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createProject = useCreateProjectMutation()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateProjectRequest>({
    resolver: zodResolver(createProjectRequestSchema),
    defaultValues: { color: PROJECT_COLORS[0] },
  })
  const selectedColor = watch('color')

  const onSubmit = handleSubmit((values) => {
    createProject.mutate(values, {
      onSuccess: (project) => {
        onClose()
        void navigate(`/projects/${project.key}`)
      },
    })
  })

  return (
    <Modal titleId="create-project-title" onClose={onClose}>
      <form onSubmit={(event) => void onSubmit(event)} noValidate className="flex flex-col gap-4">
        <h2 id="create-project-title" className="text-title font-semibold text-text">
          {t('project.create.title')}
        </h2>

        <label className="flex flex-col gap-1">
          <span className="text-meta text-text-muted">{t('project.create.nameLabel')}</span>
          <input
            autoFocus
            aria-invalid={errors.name ? true : undefined}
            className="rounded-card border border-border bg-surface-hi px-3 py-2 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-accent"
            {...register('name')}
          />
          {errors.name && (
            <span className="text-meta text-danger">{t('project.create.nameRequired')}</span>
          )}
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-meta text-text-muted">{t('project.create.keyLabel')}</span>
          <input
            aria-invalid={errors.key ? true : undefined}
            maxLength={5}
            className="w-24 rounded-card border border-border bg-surface-hi px-3 py-2 text-body uppercase text-text outline-none focus-visible:ring-2 focus-visible:ring-accent"
            {...register('key', {
              setValueAs: (value: unknown) =>
                typeof value === 'string' ? value.toUpperCase() : value,
            })}
          />
          {errors.key && (
            <span className="text-meta text-danger">{t('project.create.keyInvalid')}</span>
          )}
        </label>

        <fieldset className="flex flex-col gap-1">
          <legend className="text-meta text-text-muted">{t('project.create.colorLabel')}</legend>
          <div className="flex flex-wrap gap-2 pt-1">
            {PROJECT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={color}
                aria-pressed={selectedColor === color}
                onClick={() => setValue('color', color)}
                style={{ backgroundColor: color }}
                className={`h-6 w-6 rounded-card outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
                  selectedColor === color
                    ? 'ring-2 ring-text ring-offset-2 ring-offset-surface'
                    : ''
                }`}
              />
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1">
          <span className="text-meta text-text-muted">{t('project.create.descriptionLabel')}</span>
          <textarea
            rows={2}
            className="resize-none rounded-card border border-border bg-surface-hi px-3 py-2 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-accent"
            {...register('description')}
          />
        </label>

        {createProject.isError && (
          <p role="alert" className="text-meta text-danger">
            {t('project.create.error')}
          </p>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-card px-4 py-2 text-body text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={createProject.isPending}
            className="rounded-card bg-accent px-4 py-2 text-body font-semibold text-text transition-colors duration-100 hover:bg-accent-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
          >
            {createProject.isPending ? t('project.create.submitting') : t('project.create.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
